import { watch } from 'vue';
import { useAuthStore } from '../stores/auth';

declare global {
  interface Window {
    Beacon?: (command: string, data?: any) => void;
  }
}

interface CachedSignature {
  uid: string;
  email: string;
  signature: string;
  expires: number;
}

const CACHE_KEY = 'helpscout_signature';

export function useHelpScout() {
  const authStore = useAuthStore();

  function getCachedSignature(uid: string, email: string): string | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const data: CachedSignature = JSON.parse(cached);

      // Check if uid matches, email matches, and not expired
      if (data.uid === uid && data.email === email && Date.now() < data.expires) {
        return data.signature;
      }

      // Cache invalid, clear it
      localStorage.removeItem(CACHE_KEY);
      return null;
    } catch (error) {
      console.error('Error reading cached HelpScout signature:', error);
      return null;
    }
  }

  function setCachedSignature(uid: string, email: string, signature: string, expires: number) {
    try {
      const data: CachedSignature = { uid, email, signature, expires };
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error caching HelpScout signature:', error);
    }
  }

  function clearCachedSignature() {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.error('Error clearing cached HelpScout signature:', error);
    }
  }

  async function identifyUser() {
    if (!window.Beacon || !authStore.isAuthenticated || !authStore.user) {
      return;
    }

    const uid = authStore.user.uid;
    const email = authStore.user.email;
    const name = authStore.user.displayName;

    if (!email) {
      console.warn('Cannot identify HelpScout user: no email');
      return;
    }

    const data = { email } as any;
    if (name) data.name = name;

    try {
      // Check cache first
      let signature = getCachedSignature(uid, email);

      if (!signature && authStore.user.emailVerified) {
        // Get auth token
        const token = await authStore.getIdToken();
        if (!token) {
          console.warn('Cannot identify HelpScout user: no auth token');
          return;
        }

        // Fetch signature from backend if email is verified
        const response = await fetch(`/api/helpscout/signature`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          console.error('Failed to fetch HelpScout signature:', response.statusText);
          return;
        }

        const apiResponse = await response.json();
        signature = apiResponse.signature;

        if (!signature) {
          console.error('No signature returned from server');
          return;
        }

        // Cache the signature
        setCachedSignature(uid, email, signature, apiResponse.expires);
      }

      // Identify user with Beacon
      window.Beacon('identify', data);
    } catch (error) {
      console.error('Error identifying HelpScout user:', error);
    }
  }

  function logoutUser() {
    if (window.Beacon) {
      window.Beacon('logout');
    }
    // Clear cached signature on logout
    clearCachedSignature();
  }

  // Watch for auth state changes and identify/logout accordingly
  watch(
    () => (authStore.loading ? undefined : authStore.isAuthenticated),
    isAuthenticated => {
      if (isAuthenticated) {
        identifyUser();
      } else if (isAuthenticated === false) {
        logoutUser();
      }
    },
    { immediate: true }
  );

  return {
    identifyUser,
    logoutUser,
  };
}
